import { cn, Tooltip } from "@sg/ui";
import clsx from "clsx";
import { FunctionComponent, ReactNode, useMemo, useState } from "react";
import { matchRoutes, NavLink, useLocation, useParams } from "react-router-dom";
import useAuthStore from "~/store/auth";
import useSettingPrefStore from "~/store/setttingPref";
import { SidebarItems } from "./useSidebarItems";

function useIsInSidebarGroup(items: SidebarItems) {
  const currentLocation = useLocation();

  return useMemo(() => {
    const routes =
      items.subitems?.map((c) => ({
        path: c.to,
      })) || [];
    const matches = matchRoutes(routes, currentLocation) || [];
    return matches.length > 0;
  }, [currentLocation, items.subitems]);
}

function useGetSidebarItem() {
  const { orgId } = useParams();
  const { org_id } = useAuthStore((state) => state.userInfo);
  const entity = useSettingPrefStore((state) => state.entity) || "";

  return ({ to, ...item }: SidebarItems) => {
    return {
      ...item,
      to: to ? to.replace(":orgId", orgId || org_id).replace(":cpId", entity) : "",
    };
  };
}

const MAX_NAME_LENGTH = 18;

function getName(tempName: string) {
  const trimmed = tempName.length > MAX_NAME_LENGTH;
  const name = `${tempName.substring(0, MAX_NAME_LENGTH)}${trimmed ? "..." : ""}`;

  return { trimmed, name };
}

export function SidebarCategory({ label }: { label: string }) {
  return <div className="text-xs font-semibold leading-6 text-zinc-400">{label}</div>;
}

function CoreSidebarItem({
  Icon,
  subheading,
  header,
  action,
  children,
  defaultOpen,
  className,
  onClick,
}: {
  defaultOpen?: boolean;
  Icon?: FunctionComponent;
  subheading?: string | null;
  header?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const [checked, setChecked] = useState<boolean | undefined>(defaultOpen || undefined);

  return (
    <label className={className} onClick={onClick}>
      <input
        checked={checked}
        onChange={(e) => {
          setChecked(e.target.checked);
        }}
        className="peer/showLabel absolute scale-0"
        type="checkbox"
      />
      <span
        className={cn(
          "block max-h-11 max-w-xs overflow-hidden rounded-lg control-gradient px-4 py-0 transition-all duration-300",
          {
            "peer-checked/showLabel:max-h-[unset]": !!children,
          },
        )}
      >
        <h3 className="h-11 flex items-center cursor-pointer text-sm leading-[22px] font-medium">
          {Icon && (
            <div className="-ml-2 mr-3">
              <Icon />
            </div>
          )}
          <div className="flex flex-col">
            {subheading && <div className="text-[10px] leading-3 text-white opacity-60 font-normal">{subheading}</div>}
            {header}
          </div>
          {action && <div className="ml-auto">{action}</div>}
        </h3>
        {children && <p className="pb-2 pt-[6px]">{children}</p>}
      </span>
    </label>
  );
}

export function SidebarItem(props: SidebarItems) {
  const getResolvedSidebarItem = useGetSidebarItem();

  const { name: tempName, to, Icon, subitems, disabled, subheading, action, core } = getResolvedSidebarItem(props);
  const expandedByDefault = useIsInSidebarGroup(props);

  const { trimmed, name } = getName(tempName);

  if (subitems) {
    return (
      <CoreSidebarItem
        defaultOpen={expandedByDefault}
        Icon={Icon}
        subheading={subheading}
        action={action}
        header={
          trimmed ? (
            <Tooltip content={tempName}>
              <div>{name}</div>
            </Tooltip>
          ) : (
            <div>{name}</div>
          )
        }
      >
        {subitems.map((sI) => {
          const subItem = getResolvedSidebarItem(sI);
          const { trimmed, name } = getName(sI.name);

          return (
            <li key={subItem.id || subItem.name}>
              {subItem.to ? (
                <NavLink
                  to={subItem.to}
                  end
                  className={({ isActive }: { isActive: boolean }) =>
                    clsx(
                      subItem.className,
                      isActive ? "text-primary font-medium" : "hover:text-white text-white/50",
                      { "pointer-events-none opacity-40": subItem.disabled },
                      "block rounded-md px-2 font-normal text-sm leading-[22px]",
                    )
                  }
                >
                  <div className="flex items-center justify-between">
                    <span className={subItem.textClassName}>
                      {trimmed ? <Tooltip content={sI.name}>{name}</Tooltip> : name}
                    </span>
                    {subItem.action}
                  </div>
                </NavLink>
              ) : (
                <button
                  type="button"
                  onClick={subItem.onClick}
                  className={clsx(
                    subItem.className,
                    subItem.active ? "text-primary font-medium" : "hover:text-white text-white/50",
                    "w-full block rounded-md px-2 text-sm font-normal leading-[22px] bg-transparent text-left whitespace-nowrap",
                    { "pointer-events-none opacity-40": subItem.disabled },
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className={subItem.textClassName}>
                      {trimmed ? (
                        <Tooltip content={sI.name}>
                          <span>{name}</span>
                        </Tooltip>
                      ) : (
                        name
                      )}
                    </span>
                    {subItem.action}
                  </div>
                </button>
              )}
            </li>
          );
        })}
      </CoreSidebarItem>
    );
  }

  if (core) {
    return (
      <NavLink to={to}>
        {({ isActive }: { isActive: boolean }) => (
          <span
            className={clsx(
              "block max-h-11 max-w-xs overflow-hidden rounded-lg control-gradient px-4 py-0 transition-all duration-300 peer-checked/showLabel:max-h-[unset]",
              isActive ? "!text-primary font-medium" : "hover:text-white text-white",
              {
                "pointer-events-none opacity-40": disabled,
              },
            )}
          >
            <h3 className="h-11 flex items-center cursor-pointer text-sm leading-[22px] font-medium text-current">
              {Icon && (
                <div className="-ml-2 mr-3">
                  <Icon />
                </div>
              )}
              <span className="text-current">{name}</span>
            </h3>
          </span>
        )}
      </NavLink>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          isActive && !disabled ? "bg-accent text-primary" : "text-white",
          "group flex gap-x-3 rounded-md p-2 text-sm leading-[22px] font-semibold",
          {
            "pointer-events-none": disabled,
          },
        )
      }
    >
      {trimmed ? (
        <Tooltip content={tempName}>
          <div className="pointer-events-auto">{name}</div>
        </Tooltip>
      ) : (
        <div>{name}</div>
      )}
    </NavLink>
  );
}
